import { InflateRequestMode } from '@sccs/types';
import { FilterCriteriaAlternative } from '../http/clients/dls/DlsClientAlternative';
import { FilterCriteriaDlsSearch } from '../http/clients/dls/DlsClientSearch';
import { FilterCriteriaCampaign, UnifiedTeaserFilterCriteria } from '../http/clients/dls/DlsClientTeaser';
import { OneSearchVehicleTileDataParams } from '../http/clients/onesearch/graphql/OneSearchGraphQL';

import { SharedRequestConfiguration } from '../types/GeneralParameter';
import {
  COMMERCIAL_VANS,
  vanEnumsCompatMap,
  vehicleAssortmentCommercial,
  VehicleCategory,
  vehicleCategoryCommercial,
} from './VansCompatMap';

type VanConfigTypes =
  | FilterCriteriaDlsSearch
  | UnifiedTeaserFilterCriteria
  | FilterCriteriaAlternative
  | OneSearchVehicleTileDataParams;

export function isCommercialVanBy(modelIdentifier: string): boolean {
  return COMMERCIAL_VANS.includes(modelIdentifier);
}

export function isVanBy(mode: InflateRequestMode): boolean {
  return [
    InflateRequestMode.PI_PAGES_VANS,
    InflateRequestMode.HOMEPAGE_VANS,
    InflateRequestMode.CAMPAIGN_TEASER_VANS,
  ].includes(mode);
}

export function isOneSearchVehicleTileDataParam(
  parameter: VanConfigTypes,
): parameter is OneSearchVehicleTileDataParams {
  return (parameter as OneSearchVehicleTileDataParams).typeClass !== undefined;
}

// Two modes: handle single van and handle multiple vans
export function createVanConfiguration(parameter: VanConfigTypes, configuration?: SharedRequestConfiguration) {
  if (isOneSearchVehicleTileDataParam(parameter) && parameter.typeClass?.length) {
    const vehicleCategory: VehicleCategory = configuration?.customerGroup === 'COMMERCIAL' ? 'VANS' : 'PASSENGER-CARS';
    return {
      vehicleCategory,
      modelIdentifier: '',
      bodyType: '',
      fuelType: '',
    };
  }
  const { identifier, length } = getModelIdentifierStats(parameter);
  if (length > 1) {
    return createMultipleVansConfig(parameter, identifier);
  } else {
    return createSingleVanConfig(parameter, identifier);
  }
}

function createSingleVanConfig(_parameter: VanConfigTypes, modelIdentifier: string | null) {
  const sanitizedModelIdentifier = modelIdentifier?.replace('-', '_') ?? 'ALL';
  const { identifier, body, assortment, fuelType, clusteringCriteria, vehicleCategory } =
    vanEnumsCompatMap[sanitizedModelIdentifier];
  const baseConfig = {
    modelIdentifier: identifier,
    bodyType: body,
    fuelType: fuelType.join(','),
    clusteringCriteria: clusteringCriteria,
  };
  return {
    ...baseConfig,
    vehicleCategory: vehicleCategory ?? vehicleCategoryCommercial,
    vehicleAssortment: assortment ?? vehicleAssortmentCommercial,
  };
}

function createMultipleVansConfig(parameter: VanConfigTypes, modelIdentifier: string | null) {
  const sanitizedFuelTypeSet = new Set<string>();
  const sanitizedClusteringCriteriaSet = new Set<string>();

  const bodyType = isFilterCriteriaCampaign(parameter) ? parameter.bodyType : '';
  const modelIdentifierArray = modelIdentifier?.split(',') ?? [];

  let sanitizedVehicleAssortment = '';
  let sanitizedVehicleCategory = '';

  for (const modelId of modelIdentifierArray) {
    const { assortment, fuelType, clusteringCriteria, vehicleCategory } = vanEnumsCompatMap[modelId];
    fuelType.forEach((type) => sanitizedFuelTypeSet.add(type));
    (clusteringCriteria ?? ['']).forEach((criteria) => sanitizedClusteringCriteriaSet.add(criteria));
    sanitizedVehicleAssortment = assortment ?? vehicleAssortmentCommercial;
    sanitizedVehicleCategory = vehicleCategory ?? vehicleCategoryCommercial;
  }

  const baseConfig = {
    modelIdentifier,
    bodyType: bodyType,
    fuelType: Array.from(sanitizedFuelTypeSet).join(','),
    clusteringCriteria: Array.from(sanitizedClusteringCriteriaSet),
  };
  return {
    ...baseConfig,
    vehicleCategory: sanitizedVehicleCategory,
    vehicleAssortment: sanitizedVehicleAssortment,
  };
}

/**
 * Previosly checked against type assertions e.g. if (parameter as FilterCriteriaDlsSearch) which does not work
 * changed to check against the type of modelIdentifier: If modelIdentifier is an array we join it, if not we simply return it
 * follows the previous logic for now
 */
function getModelIdentifierStats(parameter: VanConfigTypes): { identifier: string | null; length: number } {
  if (Array.isArray(parameter.modelIdentifier)) {
    const preprocessedModelidentifier = parameter.modelIdentifier.flatMap((identifiers) => {
      if (typeof identifiers === 'string') {
        return identifiers.split(',');
      } else {
        return identifiers;
      }
    });
    const identifier = preprocessedModelidentifier.join(',');
    const length = preprocessedModelidentifier.length;
    return { identifier, length };
  }
  return { identifier: parameter.modelIdentifier || null, length: 1 };
}

function isFilterCriteriaCampaign(
  criteria:
    | FilterCriteriaDlsSearch
    | UnifiedTeaserFilterCriteria
    | FilterCriteriaAlternative
    | OneSearchVehicleTileDataParams,
): criteria is FilterCriteriaCampaign {
  return 'campaign' in criteria;
}
